/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  Component,
  OnInit,
  ViewChild
}                                from '@angular/core';

import { Router }                from '@angular/router';

import {
  ValidationErrors,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  AsyncValidatorFn,
  AbstractControl
}                                from '@angular/forms';

import {
  Observable,
  of
}                                from 'rxjs';

import {
  first,
  map,
  switchMap
} from "rxjs/operators";

import { IUserRow }              from '@/screens/administration/i.user.row';
import { Logger }                from '@/util/logger';
import { MatSidenav }            from '@angular/material/sidenav';
import { UserService }           from '@/services/user.service';

@Component({
  templateUrl: 'new.user.page.component.html'
})

/**
 *
 * @class NewUserPageComponent
 *
 * @brief create a new (baked in) user account
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2021
 *
 */
export class NewUserPageComponent implements OnInit {

  @ViewChild('adminsidenav')
  public         nav: MatSidenav;

  /** any changes for the user attributes */
  public newUser: IUserRow   = null;

  /** validation controller for the form */
  public  form: UntypedFormGroup    = null;

  /** confirmation of saving */
  public createMessage: string = '';

  /** confirmation of password change */
  public resetMessage: string = '';

  /** the new password */
  public _password: string = '';

  /** standard constructor */
  constructor(
    private router: Router,
    private users:  UserService
  ) {

  }

  /** on page setup, create the form validator */
  ngOnInit() {

    Logger.debug('[NewUser] init...');


    let nu: NewUserPageComponent = this;

    /* fill in an empty user object */

    nu.newUser = {
      userid:       '',
      email:        '',
      display_name: '',
      last_name:    '',
      first_name:   '',
    } as IUserRow;

    /* setup the validation for the form ... */

    nu.form = new UntypedFormGroup({

      name: new UntypedFormControl('', [
        Validators.required,
      ]),

      firstname: new UntypedFormControl('', [
        Validators.required,
      ]),

      lastname: new UntypedFormControl('', [
        Validators.required,
      ]),

      email: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$")
        ],
        this.emailExistsValidator()
      ),

      userid: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern("^[a-zA-Z0-9._%+-]+$")
        ],
        this.useridExistsValidator()
      ),

      password: new UntypedFormControl('', [Validators.required])
    });

    nu.form.statusChanges.subscribe((status: string) => {

      Logger.debug(`[NewUser] user form status: `, status);

    });

  }

  /** server side validation of email, make sure its not already assigned to another user. */
  private emailExistsValidator(): AsyncValidatorFn {

    let nu: NewUserPageComponent = this;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {

      return of(control.value).pipe(
        switchMap((email) => nu.users.userByEmail(email).pipe(
          map((row: IUserRow) => {
            return row ? { exists: true } : null;
          })
        ))
      );
    };
  }

  /** server side validation of usuerid, make sure its not already assigned to another user.*/
  private useridExistsValidator(): AsyncValidatorFn {

    let nu: NewUserPageComponent = this;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {

      return of(control.value).pipe(
        switchMap((userid) => nu.users.userByUserid(userid).pipe(
          map((row: IUserRow) => {

            return row ? {exists: true} : null;
          })
        ))
      );
    };
  }

  /** helper to update the form display */
  private patch(user: IUserRow) {

    this.form.patchValue({
      name:      user.display_name,
      firstname: user.first_name,
      lastname:  user.last_name,
      email:     user.email,
      userid:    user.userid
    });

  }

  /** helper to update a field in the newUser row */
  updateNewUser(field: string, value: any) {

    this.createMessage = '';

    if (!this.newUser) {

      return ;
    }

    this.newUser[field] = value;
  }

  /** update the name */
  setName(event: any) {

    this.updateNewUser('display_name', event.target.value);
  }

  /** update the first name */
  setFirstName(event: any) {

    this.updateNewUser('first_name', event.target.value);
  }

  /** update the last name */
  setLastName(event: any) {

    this.updateNewUser('last_name', event.target.value);
  }

  /** update the email */
  setEMail(event: any) {

    this.updateNewUser('email', event.target.value);
  }

  /** update the email */
  setUserid(event: any) {

    this.updateNewUser('userid', event.target.value);
  }

  /** update the password reset value */
  setPassword(event: any) {

    this._password = event.target.value;
  }

  /** getter for display_name */
  get name() { return this.form.get('name'); }

  /** getter for first_name */
  get firstname() { return this.form.get('firstname'); }

  /** getter for last_name */
  get lastname() { return this.form.get('lastname'); }

  /** getter for email */
  get email() { return this.form.get('email'); }

  /** getter for userid */
  get userid() { return this.form.get('userid'); }

  /** getter for password */
  get password() { return this.form.get('password')}

  /** getter for the profile picture */
  profilePicture() {

    if (this.newUser && this.newUser.options && this.newUser.options.picture) {

      return this.newUser.options.picture;
    }

    return 'assets/images/profile-512.png';
  }

  /** open or close the side nav panel */
  toggleSidebar() {

    this.nav.toggle();
  }

  /** update the database */
  create() {

    Logger.debug(`[NewUser] creating: `, this.newUser);

    let nu: NewUserPageComponent = this;
    let edit: any = {};

    ['userid', 'email', 'display_name', 'last_name', 'first_name'].forEach( (field) => {

      edit[field] = nu.newUser[field];

    });

    edit['is_active']    = true;
    edit['is_suspended'] = false;
    edit['is_pending']   = false;

    /* set the password; if they are providing an override */

    if (nu._password.length > 0) {

      edit['password']   = nu._password;
    }

    nu.users.create(edit)
      .pipe(first())
      .subscribe(
        (row: IUserRow) => {

          Logger.debug(`[NewUser] saved: `, row);

          nu.newUser     = row;

          /* update the form control */

          nu.patch(nu.newUser);

          nu.createMessage = "Created!";

          /* after approval, go back to the user list page */

          this.router.navigate(['administration/users'])

        },
        error => {

          Logger.error(`[NewUser] could not save: `, error);

          nu.createMessage = error.toString();

        });

  }

}
